/* eslint-disable no-restricted-syntax -- this file defines the tokens */
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import {
  StyledEngineProvider,
  ThemeProvider,
  alpha,
  unstable_createMuiStrictModeTheme as createMuiTheme,
  type Breakpoint,
  type CSSObject,
  type Theme,
} from '@mui/material/styles';
import type { CSSProperties } from '@mui/material/styles/createMixins';
import createPalette from '@mui/material/styles/createPalette';
import createTypography from '@mui/material/styles/createTypography';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { LinkBehavior } from '~/components/link';
import { useNonce } from '~/hooks/csp';

// ============================================================================
// CONSTANTS
// ============================================================================

// Base Colors
const WHITE = '#FFFFFF';
const BLACK = '#000000';

// Brand Colors
// 3.75:1 on white either way round: the nav fill, borders, charts and the Studio canvas chrome,
// never body-size text or a label on itself (WCAG 1.4.3). primary.text is its text-size
// stand-in; filled controls under white labels use primary.dark.
const BRAND_MAIN = '#2B8ACB';
const BRAND_DARK = '#003F72'; // 10.75:1 under white text

// Grays
const GRAY_50 = '#F9FAFA'; // Light hover state
const GRAY_100 = '#F8F8F8'; // Default background
// Search box, segmented filters, AI chat input. Load-bearing for contrast: it is the
// darkest surface secondary text sits on, so it sets the floor for GRAY_500.
const GRAY_150 = '#eeeeee';
const GRAY_200 = '#e8e8e8'; // Main border
const GRAY_300 = '#e6e6e6'; // Light border
const GRAY_400 = '#dedcdc'; // Dark border
// Control border: the boundary of a text field or outlined button must clear 3:1 against the
// white and grey surfaces either side of it (WCAG 1.4.11). 3.6:1 on white, 3.1:1 on GRAY_150.
const GRAY_450 = '#868686';
// Secondary text. Dark enough for 4.5:1 on the #eeeeee field backgrounds too, not just
// on white — the search placeholder and segmented filter sit on those (WCAG 1.4.3).
const GRAY_500 = '#6C6C6C';

// Primary Blues
const BLUE_700 = '#277cb7'; // Selected fill (counts, MultiChip, upload progress). 4.51:1 under white
const BLUE_600 = '#4096d0'; // Primary hover
const BLUE_500 = '#00325b'; // Secondary dark, focus ring
// Hover for primary.dark fills: lighter than Midnight Blue without jumping to Cerulean. 6.66:1 under white
const BLUE_550 = '#1f5f99';
const BLUE_400 = '#33658e'; // Secondary light
const BLUE_300 = '#9FD8FF'; // Primary light
// De-emphasised foreground: nav labels/icons on primary.main. A known 1.4.3 failure at 2.83:1 - tokenised so
// the eventual fix is one value.
const BLUE_200 = '#BFE5FF';
const BLUE_100 = '#D0EFFF'; // Toggle active, row selected

// Success Greens
// success.main carries both status text and white-on-green fills, so it clears 4.5:1 both ways
// (WCAG 1.4.3). The previous #11D300 was 2.03:1 - failing as text AND under its own white
// contrastText. dark follows it down to stay the darker of the pair.
const GREEN_900 = '#1B5E20'; // 7.87:1 on white
const GREEN_700 = '#2E7D32'; // 5.13:1 on white, 5.13:1 under white text
const GREEN_500 = '#68ff4d'; // 1.31:1 - decorative only, never text or a boundary

// Warning Orange
// main is 3.11:1 on white and dark 3.79:1 - large text and non-text only, never body copy (WCAG 1.4.3).
// ORANGE_800 is the text-size and filled-chip shade: 5.43:1 on white, 4.68:1 on GRAY_150, 5.43:1
// under white text. ORANGE_850 is its hover, 6.86:1 under white.
const ORANGE_900 = '#e65100';
const ORANGE_850 = '#944300';
const ORANGE_800 = '#AD4E00';
const ORANGE_700 = '#ED6C02';
const ORANGE_500 = '#ff9800';

// Info Blues - distinct from the brand blues above
// main is 3.86:1 under white, so filled info chips use LIGHT_BLUE_900 (7.4:1) with
// LIGHT_BLUE_950 (9.98:1) as their hover.
const LIGHT_BLUE_950 = '#014479';
const LIGHT_BLUE_900 = '#01579b';
const LIGHT_BLUE_700 = '#0288d1';
const LIGHT_BLUE_500 = '#03a9f4';

// Tertiary Purples
const PURPLE_900 = '#311b92';
const PURPLE_500 = '#673ab7';
const PURPLE_200 = '#b39ddb';

// Error Reds
// Both carry error text and white-on-red buttons, so both clear 4.5:1 (WCAG 1.4.3)
const RED_700 = '#C62828'; // rgb(198, 40, 40)
const RED_600 = '#D32F2F'; // rgb(211, 47, 47)
const RED_400 = '#ef5350'; // 3.76:1 on white - decorative, never text

// Device kinds carry the vendor's own brand mark - Android green, Samsung blue, LG red. They
// identify a platform rather than a UI state, so they never track the palette. They live here
// so the dashboard legend and the reporting charts cannot drift apart, which they already had.
const DEVICE_KINDS = {
  android: '#3DDC84',
  brightsign: '#E91E63',
  linux: '#FCC624',
  samsung: '#1428A0',
  web: '#4285F4',
  webos: '#A50034',
} as const;

// Report chart series. A categorical palette: these need to be distinguishable from each other,
// which is a different requirement than contrast against a background, so they are not drawn
// from the semantic colors above. The first five happen to match device kinds - kept separate
// deliberately, since the two lists answer to different needs.
const CHART_SERIES = [
  '#3DDC84',
  '#E91E63',
  '#FCC624',
  '#1428A0',
  '#4285F4',
  '#FF7043',
  '#AB47BC',
  '#26C6DA',
  '#9CCC65',
  '#78909C',
  '#B0BEC5',
];

// Placeholder illustrations (layout skeletons, orientation icons). Artwork greys with no
// semantic meaning - named so the components that draw them hold no literals.
const SKELETON_FILL = '#dadada';
const SKELETON_ZONE = '#E3E3E3';

// Studio editor chrome. Off-palette today; folding them into the semantic colors would be a
// visual change, so they are named here rather than left scattered as literals.
const STUDIO_CANVAS = '#ebecf0';
// Fill a new shape gets when dropped on the canvas. A starting value the user then edits,
// stored in their design - changing it affects new shapes only, never existing ones.
const STUDIO_SHAPE_FILL = '#dddddd';
const STUDIO_LAYER_HIGHLIGHT = '#E3F2FD';
const STUDIO_RULER_TEXT = '#1b3a61';
const STUDIO_TICK = '#bcc5d3';
const STUDIO_PLACEHOLDER = '#ccc';

// Keyboard focus indicator. Clears 3:1 against every surface it is drawn on: 11:1+ on the white
// and grey content surfaces, 3.48:1 on the primary.main nav (WCAG 2.4.7, 1.4.11). The outset ring
// leaves a 2px gap so it borders the surrounding surface, never the control's own fill, which is
// what keeps it legible next to dark fills like primary.dark (1.22:1 if they touched).
const FOCUS_RING = BLUE_500;

// ============================================================================
// TYPE DECLARATIONS
// ============================================================================

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    field: string;
  }
  // Optional because only some colors define them. `text` is the shade for body-size text, set
  // where main itself is under 4.5:1 on the page backgrounds.
  interface PaletteColor {
    darkHover?: string;
    hover?: string;
    mutedText?: string;
    selected?: string;
    text?: string;
  }
  interface SimplePaletteColorOptions {
    darkHover?: string;
    hover?: string;
    mutedText?: string;
    selected?: string;
    text?: string;
  }
  interface PaletteOptions {
    tertiary?: PaletteColorOptions;
    deviceKinds?: Record<string, string>;
    skeleton?: { fill: string; zone: string };
    chartSeries?: string[];
    studio?: {
      canvas: string;
      shapeFill: string;
      layerHighlight: string;
      rulerText: string;
      tick: string;
      placeholder: string;
    };
    borders?: {
      light: string;
      main: string;
      dark: string;
      control: string;
    };
    table?: {
      backgroundColor: string;
      borderColor: string;
      checkedBackgroundColor: string;
      checkedBorderColor: string;
      focusedBackgroundColor: string;
      headerBackgroundColor: string;
      headerFontColor: string;
      hoverBackgroundColor: string;
    };
  }
  interface Palette {
    deviceKinds: Record<string, string>;
    skeleton: { fill: string; zone: string };
    chartSeries: string[];
    studio: {
      canvas: string;
      shapeFill: string;
      layerHighlight: string;
      rulerText: string;
      tick: string;
      placeholder: string;
    };
    borders: {
      light: string;
      main: string;
      dark: string;
      control: string;
    };
    table: {
      backgroundColor: string;
      borderColor: string;
      checkedBackgroundColor: string;
      checkedBorderColor: string;
      focusedBackgroundColor: string;
      headerBackgroundColor: string;
      headerFontColor: string;
      hoverBackgroundColor: string;
    };
  }
}

declare module '@mui/material/styles/createMixins' {
  interface Mixins {
    focusRing: CSSProperties;
    focusRingInset: CSSProperties;
    focusRingOnImage: CSSProperties;
    wrapBelow: (breakpoint: Breakpoint) => CSSObject;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    tertiary: true;
  }
}

// ============================================================================
// PALETTE
// ============================================================================

const primary = {
  contrastText: WHITE,
  dark: BRAND_DARK,
  main: BRAND_MAIN,
  light: BLUE_300,
  // Hover/focus for anything filled with primary.main (nav items, the network button).
  hover: BLUE_600,
  // Hover/focus for anything filled with primary.dark. Contained buttons, toggle buttons and
  // filled chips get it from their overrides; hand-rolled controls (the remote's OK button, the
  // filter toggle) reference this so they cannot drift from it.
  darkHover: BLUE_550,
  mutedText: BLUE_200,
  selected: BLUE_700,
  // Text stays black rather than blue: links carry an underline instead (WCAG 1.4.1).
  text: BLACK,
} as const;

const secondary = {
  contrastText: WHITE,
  dark: BLUE_500,
  main: BRAND_DARK,
  light: BLUE_400,
} as const;

const palette = createPalette({
  // MUI paints this behind keyboard-focused list items, accordion headers, chips and cards. The
  // focus ring marks focus now, so keep the tint at the hover grey instead of the 12% black default.
  action: { focus: 'rgba(0, 0, 0, 0.02)', focusOpacity: 0.02 },
  common: {
    black: BLACK,
    white: WHITE,
  },
  primary,
  secondary,
  // Status colours carry body-size text too (device connection status), so each has a `text`
  // shade clearing 4.5:1 on every page background, the #eeeeee field surface included.
  success: {
    contrastText: WHITE,
    dark: GREEN_900,
    main: GREEN_700,
    light: GREEN_500,
    text: GREEN_900,
  },
  info: {
    contrastText: WHITE,
    dark: LIGHT_BLUE_900,
    darkHover: LIGHT_BLUE_950,
    main: LIGHT_BLUE_700,
    light: LIGHT_BLUE_500,
    text: LIGHT_BLUE_900,
  },
  warning: {
    contrastText: WHITE,
    dark: ORANGE_900,
    darkHover: ORANGE_850,
    main: ORANGE_700,
    light: ORANGE_500,
    text: ORANGE_800,
  },
  error: {
    contrastText: WHITE,
    dark: RED_700,
    main: RED_600,
    light: RED_400,
    text: RED_700,
  },
  tertiary: {
    contrastText: WHITE,
    dark: PURPLE_900,
    main: PURPLE_500,
    light: PURPLE_200,
  },
  text: {
    primary: BLACK,
    secondary: GRAY_500,
  },
  background: {
    default: GRAY_100,
    field: GRAY_150,
    paper: WHITE,
  },
  divider: GRAY_150,
  deviceKinds: DEVICE_KINDS,
  skeleton: { fill: SKELETON_FILL, zone: SKELETON_ZONE },
  chartSeries: CHART_SERIES,
  studio: {
    canvas: STUDIO_CANVAS,
    shapeFill: STUDIO_SHAPE_FILL,
    layerHighlight: STUDIO_LAYER_HIGHLIGHT,
    rulerText: STUDIO_RULER_TEXT,
    tick: STUDIO_TICK,
    placeholder: STUDIO_PLACEHOLDER,
  },
  borders: {
    light: GRAY_300,
    main: GRAY_200,
    dark: GRAY_400,
    control: GRAY_450,
  },
  table: {
    backgroundColor: WHITE,
    borderColor: GRAY_200,
    checkedBackgroundColor: WHITE,
    checkedBorderColor: BRAND_DARK,
    focusedBackgroundColor: BLUE_100,
    headerBackgroundColor: 'transparent',
    headerFontColor: GRAY_500,
    hoverBackgroundColor: GRAY_50,
  },
});

// ============================================================================
// TYPOGRAPHY
// ============================================================================

const typography = createTypography(palette, {
  fontFamily: 'Lato, Arial, Helvetica, sans-serif',
  h1: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '35px',
    lineHeight: '40px',
  },
  h2: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '29px',
    lineHeight: '32px',
  },
  h3: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '28px',
  },
  h4: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
  },
  h5: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
  },
  h6: {
    color: palette.text.primary,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
  },
  subtitle1: {
    color: palette.text.primary,
    fontSize: '16px',
    lineHeight: '25px',
  },
  subtitle2: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '21px',
  },
  body1: {
    color: palette.text.primary,
    fontSize: '14px',
    lineHeight: '21px',
  },
  body2: {
    fontSize: '12px',
    lineHeight: '18px',
  },
  button: {
    color: palette.text.primary,
  },
  caption: {
    fontSize: '11px',
    lineHeight: '13px',
  },
  overline: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '13px',
    textTransform: 'uppercase' as const,
  },
});

// ============================================================================
// MIXINS
// ============================================================================

// Spread into a `:focus-visible` (or `.Mui-focusVisible`) rule. The 1px line carries the 3:1
// requirement; the translucent halo is for visibility only and is not counted.
const FOCUS_HALO = alpha(BRAND_MAIN, 0.3);
const focusRing = {
  outline: `1px solid ${FOCUS_RING}`,
  outlineOffset: 0,
  boxShadow: `0 0 0 4px ${FOCUS_HALO}`,
};

// For controls that fill a clipping container (menu items, tabs, grid cells), where an outset
// ring would be cut off. It sits on the control's own fill, so only use it on light fills.
const focusRingInset = {
  outline: `1px solid ${FOCUS_RING}`,
  outlineOffset: -1,
  boxShadow: `inset 0 0 0 4px ${FOCUS_HALO}`,
};

// For controls over photos and thumbnails, where no single colour clears 3:1. The shadow puts
// the ring colour either side of a white outline, so one of them always contrasts.
const focusRingOnImage = {
  outline: `1px solid ${FOCUS_RING}`,
  outlineOffset: 0,
  boxShadow: `0 0 0 2px ${WHITE}, 0 0 0 5px ${FOCUS_HALO}`,
};

// Enter toggles a checkbox or switch as Space does. Keyboard users expect Enter to work the
// focused control, and the list rows' checkbox already does this.
const toggleOnEnter = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Enter') return;
  const input = event.currentTarget.querySelector('input');
  if (!input || input.disabled) return;
  event.preventDefault();
  input.click();
};

// ============================================================================
// COMPONENTS
// ============================================================================

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      '*::-webkit-scrollbar': {
        width: '7px',
        height: '7px',
      },
      '*::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, .1)',
      },
      '*::-webkit-scrollbar-thumb': {
        background: 'rgba(0, 0, 0, .25)',
        borderRadius: '5px',
        transition: 'color .2s ease',
        cursor: 'pointer',
      },
      // Native controls outside MUI. :where() leaves just the :focus-visible (0,1,0), and global
      // styles are inserted first, so any component class that styles its own focus wins.
      ':where(a[href], button, summary):focus-visible': focusRing,
      // Inline links are plain rectangles, so the ring needs a radius of its own.
      ':where(a[href]):focus-visible': { borderRadius: 4 },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        '&:before': {
          top: 0,
        },
        '&.Mui-expanded': {
          margin: 'unset',
        },
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      content: {
        alignItems: 'center',
        display: 'flex',
        fontSize: '14px',
        fontWeight: 500,
        gap: '8px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&.Mui-expanded': {
          margin: 'unset',
        },
      },
      root: {
        backgroundColor: palette.background.default,
        borderRadius: '4px',
        '&.Mui-expanded': {
          minHeight: '48px',
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
        fontSize: '1rem',
        padding: '16px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        fontSize: '1rem',
      },
    },
  },
  MuiAvatar: {
    defaultProps: {
      // Avatars sit beside the name they illustrate, so the image is decorative unless a caller
      // passes its own alt (WCAG 1.1.1).
      alt: '',
      imgProps: { crossOrigin: 'anonymous' as const },
    },
    styleOverrides: {
      root: {
        fontSize: '1rem',
        fontWeight: 600,
        borderStyle: 'solid',
        borderWidth: 1,
      },
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          color: palette.primary.dark,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        textTransform: 'none',
      },
      // MUI's own outline is the button colour at half opacity, under 3:1 (WCAG 1.4.11).
      outlined: {
        borderColor: palette.borders.control,
        '&:hover': {
          backgroundColor: GRAY_50,
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      // Neutral secondary action: a grey fill and border that stay grey on hover, so only
      // contained buttons carry the primary colour.
      outlinedPrimary: {
        color: BLACK,
        backgroundColor: GRAY_100,
        borderColor: palette.borders.control,
        '&:hover': {
          backgroundColor: GRAY_150,
          borderColor: palette.borders.control,
          '@media (hover: none)': {
            backgroundColor: GRAY_100,
          },
        },
      },
      textPrimary: {
        color: palette.primary.text,
      },
      // Filled with primary.dark, not primary.main, so the white label clears 4.5:1.
      containedPrimary: {
        backgroundColor: palette.primary.dark,
        '&:hover': {
          backgroundColor: palette.primary.darkHover,
          // Touch screens keep :hover after a tap; MUI's own rule would put primary.main back.
          '@media (hover: none)': {
            backgroundColor: palette.primary.dark,
          },
        },
      },
      containedSecondary: {
        fontWeight: 600,
      },
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      // It fills a Card, which clips, so an outset ring would never show.
      root: {
        '&.Mui-focusVisible': focusRingInset,
      },
    },
  },
  MuiCardMedia: {
    styleOverrides: {
      img: {
        objectFit: 'contain',
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small' as const,
    },
    styleOverrides: {
      root: {
        fontWeight: 600,
      },
      // Same fills as a contained primary button, so the white label clears 4.5:1.
      filledPrimary: {
        backgroundColor: palette.primary.dark,
        '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
          backgroundColor: palette.primary.darkHover,
        },
      },
      // warning.main and info.main are under 4.5:1 beneath the white label, so filled chips take
      // the darker shades (WCAG 1.4.3).
      filledWarning: {
        backgroundColor: palette.warning.text,
        '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
          backgroundColor: palette.warning.darkHover,
        },
      },
      filledInfo: {
        backgroundColor: palette.info.dark,
        '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
          backgroundColor: palette.info.darkHover,
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        fontSize: '14px',
        borderColor: palette.borders.light,
        // Cells clip their content, which would cut an outset ring and its halo off the controls
        // inside. Inset further than usual: header checkboxes are taller than the header that
        // clips them.
        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
          '& :focus-visible, & .Mui-focusVisible': { outlineOffset: -4, boxShadow: 'none' },
        },
        // The grid's own outline also shows on mouse clicks; draw the ring for keyboard focus only.
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible':
          focusRingInset,
      },
      columnHeader: {
        height: '40px !important',
      },
      columnHeaderTitle: {
        fontWeight: 'bold',
      },
      editInputCell: {
        fontSize: '14px',
      },
      footerContainer: {
        borderTop: '0',
        height: '64px',
        minHeight: '64px',
      },
      withBorderColor: {
        borderColor: palette.borders.main,
      },
      virtualScroller: {
        minHeight: '100px',
        overflow: 'auto',
      },
      pinnedColumns: {
        backgroundColor: WHITE,
      },
      pinnedColumnHeaders: {
        backgroundColor: WHITE,
        paddingRight: '0px !important',
      },
      skeletonLoadingOverlay: {
        height: 'auto',
      },
      cell: {
        display: 'flex',
        alignItems: 'center',
      },
      cellSkeleton: {
        height: '45px !important',
        minHeight: '45px !important',
        maxHeight: '45px !important',
      },
      row: {
        '&.MuiDataGrid-rowSkeleton': {
          marginTop: '0 !important',
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        backgroundColor: GRAY_50,
        borderTop: `1px solid ${palette.borders.main}`,
        marginRight: 0,
        padding: '16px',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        backgroundColor: GRAY_50,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        minHeight: 0,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      contained: {
        marginLeft: 0,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.03)',
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      // Outlined and underlined inputs mark focus with their own border; a bare InputBase (the
      // search bar, pagination's per-page select) has nothing but the caret.
      root: {
        '&.Mui-focused:not(.MuiOutlinedInput-root, .MuiInput-underline, .MuiFilledInput-underline, .MuiDataGrid-editInputCell)':
          focusRing,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: palette.primary.text,
        // MuiFormLabel's focused rule outranks root and would bring back primary.main.
        '&.Mui-focused:not(.Mui-error, .Mui-disabled)': {
          color: palette.primary.text,
        },
      },
    },
  },
  MuiLink: {
    // A default color prop rather than a style override: Link hands its color to Typography's
    // sx, which outranks styleOverrides. It also tints the underline to match.
    defaultProps: {
      color: 'primary.text',
      // Links are black like the text around them, so the underline is what marks them.
      underline: 'always' as const,
    },
  },
  MuiList: {
    styleOverrides: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: palette.primary.dark,
        minWidth: 32,
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontWeight: 500,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        // Both focus selectors: MUI's class follows its own heuristic, the pseudo-class the browser's.
        '&:hover, &.Mui-selected:hover, &.Mui-selected, &.Mui-selected.Mui-focusVisible, &.Mui-focusVisible, &:focus-visible':
          {
            backgroundColor: GRAY_50,
          },
        '&.Mui-focusVisible, &:focus-visible': focusRingInset,
        // Disabled items can take focus, so a white veil fades their contents in place of MUI's
        // opacity, which would fade the focus ring too. The ring then moves to a layer above the
        // veil; MUI never marks a disabled item Mui-focusVisible, so only the pseudo-class applies.
        '&.Mui-disabled': {
          opacity: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundColor: alpha(WHITE, 1 - palette.action.disabledOpacity),
          },
        },
        '&.Mui-disabled:focus-visible': {
          outline: 'none',
          boxShadow: 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            ...focusRingInset,
          },
        },
      },
    },
  },
  MuiModal: {
    defaultProps: {
      // A dialog, popover or menu is its own keyboard context. Its portal keeps key events out
      // of the DOM ancestors but not the React ones, so without this a data grid actions cell
      // would move focus between its buttons on the arrow keys typed into a popover's search box.
      onKeyDown: (event: KeyboardEvent) => event.stopPropagation(),
    },
  },
  MuiOutlinedInput: {
    defaultProps: {},
    styleOverrides: {
      root: {
        // The field's own border is the focus line, so it matches every other ring.
        '&.Mui-focused': { boxShadow: focusRing.boxShadow },
        '&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline, &.Mui-focused:hover fieldset.MuiOutlinedInput-notchedOutline':
          {
            borderColor: FOCUS_RING,
            borderWidth: 1,
          },
        // An invalid field keeps its red border while focused; the halo alone marks focus.
        '&.Mui-error.Mui-focused fieldset.MuiOutlinedInput-notchedOutline, &.Mui-error.Mui-focused:hover fieldset.MuiOutlinedInput-notchedOutline':
          {
            borderColor: palette.error.main,
          },
        '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
        },
        backgroundColor: WHITE,
        '&.Mui-disabled': {
          backgroundColor: GRAY_50,
        },
        '&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: palette.borders.main,
        },
      },
      input: {
        borderColor: palette.borders.control,
      },
      notchedOutline: {
        borderColor: palette.borders.control,
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        fontSize: '14px',
      },
      // Same fills as a contained primary button, so the selected page's white number clears 4.5:1.
      textPrimary: {
        '&.Mui-selected': {
          backgroundColor: palette.primary.dark,
          '&:hover': {
            backgroundColor: palette.primary.darkHover,
            '@media (hover: none)': {
              backgroundColor: palette.primary.dark,
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: palette.primary.darkHover,
          },
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      elevation1: {
        boxShadow: '0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15)',
      },
    },
  },
  MuiSelect: {},
  MuiSlider: {
    styleOverrides: {
      root: {
        height: 5,
        padding: '15px 0',
        '& .MuiSlider-valueLabel': {
          fontSize: 14,
          fontWeight: 'normal',
          top: 0,
          backgroundColor: 'unset',
          color: palette.text.primary,
          '&::before': {
            display: 'none',
          },
          '& *': {
            background: 'transparent',
            color: BLACK,
          },
        },
        '& .MuiSlider-rail': {
          opacity: 0.5,
          boxShadow: 'inset 0px 0px 4px -2px #000',
          backgroundColor: '#d0d0d0',
        },
      },
      // The default focus halo is primary.main at 16% opacity, well under 3:1.
      thumb: {
        '&.Mui-focusVisible': focusRing,
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      lineVertical: {
        borderColor: 'transparent',
      },
      vertical: {
        height: 24,
      },
    },
  },
  // White step numbers need 4.5:1: primary.main and MUI's text.disabled grey both fall short.
  MuiStepIcon: {
    styleOverrides: {
      root: {
        color: GRAY_500,
        '&.Mui-active, &.Mui-completed': {
          color: palette.primary.dark,
        },
      },
    },
  },
  MuiStepContent: {
    defaultProps: {
      TransitionProps: {
        in: true,
      },
    },
    styleOverrides: {
      root: {
        borderColor: 'transparent',
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiTable: {
    defaultProps: {
      size: 'small' as const,
    },
    styleOverrides: {
      root: {
        backgroundColor: WHITE,
        border: `1px solid ${palette.table.borderColor}`,
        borderRadius: '6px',
        borderCollapse: 'separate',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        borderBottom: 0,
      },
      root: {
        fontSize: 16,
        borderBottom: `1px solid ${palette.table.borderColor}`,
        '&:first-of-type th, &:first-of-type td, &:last-child th, &:last-child td': {
          borderBottom: 0,
        },
      },
      sizeSmall: {
        padding: '8px 24px 6px 16px',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: palette.table.headerBackgroundColor,
        border: `1px solid ${palette.table.borderColor}`,
      },
    },
  },
  MuiTablePagination: {
    defaultProps: {
      rowsPerPageOptions: [10, 25, 50],
      component: 'div' as const,
    },
    styleOverrides: {
      actions: {
        '& > button': {
          padding: 0,
        },
      },
      displayedRows: {
        fontSize: '14px',
      },
      selectLabel: {
        fontSize: '14px',
      },
      root: {
        minHeight: 36,
      },
      select: {
        paddingBottom: 4,
        fontSize: '14px',
        fontWeight: 600,
      },
      toolbar: {
        minHeight: 36,
        paddingRight: 16,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontWeight: '700',
        letterSpacing: '0.1em',
        fontSize: '12px',
        minHeight: 36,
        minWidth: 75,
        '@media (min-width: 600px)': {
          minWidth: 75,
        },
        '&.Mui-selected': {
          color: palette.primary.text,
        },
        '&.Mui-focusVisible': focusRingInset,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${palette.borders.main}`,
        backgroundColor: WHITE,
        minHeight: 45,
        padding: '8px 48px 0',
      },

      fixed: {
        minHeight: 36,
      },
      indicator: {
        height: 3,
        backgroundColor: palette.primary.dark,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      // Same fills as a contained primary button, so the white label clears 4.5:1.
      root: {
        '&.Mui-selected:not(.Mui-disabled)': {
          color: palette.common.white,
          '&, &:hover': {
            backgroundColor: palette.primary.dark,
          },
        },
        '&&:hover': {
          backgroundColor: palette.primary.darkHover,
          color: palette.common.white,
        },
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      gutters: {
        '@media (min-width: 600px)': {
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 14,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      // The subtitle variants style secondary copy, counts and card captions, not section
      // headings, so they must not render as h6 (WCAG 1.3.1).
      variantMapping: { subtitle1: 'p', subtitle2: 'p' },
    },
    styleOverrides: {
      gutterBottom: {
        marginBottom: 8,
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      size: 'small' as const,
      onKeyDown: toggleOnEnter,
    },
  },
  MuiSwitch: {
    defaultProps: {
      onKeyDown: toggleOnEnter,
    },
    styleOverrides: {
      // The root clips the thumb's ring, so ring the whole switch as a pill instead.
      root: {
        '&:has(.Mui-focusVisible)': { ...focusRingInset, borderRadius: 999 },
      },
      switchBase: {
        '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
      },
      // MUI's off track is #9e9e9e (2.7:1); the off state needs 3:1 (WCAG 1.4.11).
      track: {
        '.MuiSwitch-switchBase:not(.Mui-checked):not(.Mui-disabled) + &': {
          backgroundColor: palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true, // No more ripple, on the whole application 💣!
      LinkComponent: LinkBehavior,
    },
    // The ripple is also MUI's keyboard focus indicator, so it needs replacing.
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': focusRing,
      },
    },
  },
};

// ============================================================================
// THEME CREATION
// ============================================================================

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 768,
    md: 900,
    lg: 1280,
    xl: 1920,
  },
});

// Text that ellipsizes on wide screens, where it almost always fits, and wraps below the given
// breakpoint, where an ellipsis would hide most of it with no non-hover way to read it.
const wrapBelow = (breakpoint: Breakpoint): CSSObject => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [breakpoints.down(breakpoint)]: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
});

export const theme: Theme = createMuiTheme({
  palette,
  components,
  mixins: { focusRing, focusRingInset, focusRingOnImage, wrapBelow },
  spacing: 8,
  typography,

  breakpoints,

  zIndex: {
    appBar: 1200,
    drawer: 1100,
  },
});

// ============================================================================
// THEME PROVIDER COMPONENT
// ============================================================================

export interface ThemeProps {
  children: ReactNode;
}

export const ThemeWrapper = ({ children }: ThemeProps) => {
  const nonce = useNonce();
  const cache = useRef(createCache({ key: 'neo', nonce, prepend: true })).current;

  return (
    <StyledEngineProvider injectFirst>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </StyledEngineProvider>
  );
};
